import { DeepValue, DotSelector } from '../types';
import { distinctUntilChanged, filter, map, scan } from 'rxjs/operators';
import {
    ExtractionResult,
    ExtractionSelector,
    FULL_STATE,
    ObjectPropType,
    Predicate,
    PropertyContainerObjectType,
} from '../interfaces/functions.interface';
import { mergeToObject, referenceEqualer, referenceEqualerCurried } from './object-utils';
import { MonoTypeOperatorFunction, Observable, OperatorFunction, pipe } from 'rxjs';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';

export const pluckTyped = <T, P extends DotSelector<T> & string>
(property: P, fallBack: DeepValue<T, P> = undefined): OperatorFunction<T, DeepValue<T, P>> => {
    return pipe(
        map(target => get(target, property, fallBack)),
        cast<DeepValue<T, P>>(),
    );
};

export const compact = function<T>(): MonoTypeOperatorFunction<T>{
    return filter(data => Boolean(data));
};

/**
 * RxJs operator to concatenate emitting from observable arrays
 * @param reverse indicate how new value will be added to result
 */
export const accumulateArray = <T>(reverse = false): MonoTypeOperatorFunction<Array<T>> =>
    scan<Array<T>, Array<T>>((acc, val) => {
        return reverse ? [ ...val, ...acc ] : [ ...acc, ...val ];
    }, []);

export const terminate = <T>(predicate: Predicate<T>) => (source: Observable<T>): Observable<T> => {
    return new Observable(subscriber => {
        const subscription = source.subscribe({
            next(value) {
                subscriber.next(value);
                if (Boolean(predicate) && predicate(value)){
                    subscriber.complete();
                }
            },
            error(error) {
                subscriber.error(error);
            },
            complete() {
                subscriber.complete();
            },
        });

        return () => subscription.unsubscribe();
    });
};

/**
 * find by predicate and pick by property
 * if not found return undefined
 */
export const findAndPluck = <Type extends PropertyContainerObjectType, Property extends ObjectPropType<Type>>
(prop: Property, predicate: Predicate<Type>): OperatorFunction<Array<Type>, Type[Property] | undefined> =>
    map(list => list.find(predicate)?.[ prop ]);

/**
 * get property and filter by it
 */
export const pluckAndFilter = <T, P extends keyof T>
(prop: P, predicate: Predicate<T[P]>): MonoTypeOperatorFunction<T> =>
    filter((value) => predicate(value[ prop ]));

/**
 * cast incomming pipe to argument type
 */
export const cast = <T>(): OperatorFunction<unknown, T> => map(value => value as T);

/**
 * accumulate partial on state to full object
 * @param defaultValue DEFAULT VALUE to start with
 */
export const accumulate = <T>(defaultValue: T): OperatorFunction<Partial<T>, T> =>
    scan(mergeToObject, defaultValue);

/**
 * Added to object new property with value
 * @param key key of property
 * @param val value of new property
 */
export const addPropOperator = <Tin, K extends string, V>(key: K, val: V): OperatorFunction<Tin, Tin & Record<K, V>> =>
    map((subj: Tin) => ({ ...subj, ...{ [ key ]: val } as Record<string, V> }));

/**
 * Operator to wrap stream value in property of object
 * @param subj destination object
 * @param key new property name
 */
export const addPropTo = <T, K extends string, V>(subj: T, key: K): OperatorFunction<V, T & Record<K, V>> =>
    map((prop: V) => ({ ...(subj ?? {} as T), ...{ [ key ]: prop } as Record<K, V> }));

/**
 * Extract from input object full\partial\only one prop
 * @param request Can be ALL, Array of properties, one property name, Works only with flat objects
 */
export const partialExtractor = <T, K extends ExtractionSelector<T>>(request: K) =>
    (state: T) : ExtractionResult<T, K> => {
        if (request === FULL_STATE) {
            return state as ExtractionResult<T, K>;
        } else if (Array.isArray(request)) {
            return pick(state, request) as ExtractionResult<T, K>;
        } else {
            return state[ request as keyof T ] as ExtractionResult<T, K>;
        }
    };

/**
 * provide only changed partial original state
 * @param request Can be ALL, Array of properties, one property name
 */
export const observableSelector = <T, K extends ExtractionSelector<T>>(request: K): OperatorFunction<T, ExtractionResult<T, K>> =>
    pipe(
        map(partialExtractor(request)),
        distinctUntilChanged(Array.isArray(request) ? isEqual : referenceEqualer),
    );

/**
 * filter incoming observable by compare with fixed value
 * @param sample value to compare
 */
export const filterReference = <T>(sample: T): MonoTypeOperatorFunction<T> => filter(referenceEqualerCurried(sample));
